import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CountWidgetSettings } from 'app/shared/models/count-widget-settings';
import { StatisticsService } from 'app/shared/services/statistics.service';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { StateService } from 'app/shared/services/state.service';
import { UserService } from 'app/shared/services/user.service';
import { Subscription } from 'rxjs';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { LabelService } from '../label.service';
import { take } from 'rxjs/operators';
import { User } from 'app/shared/models/user';
import { StatusService } from 'app/shared/services/status.service';
import { LookupService } from 'app/shared/services/lookup.service';
import { CategoryService } from 'app/shared/services/category.service';

@Component({
  selector: 'subject-count-widget',
  styles: ['.countwidget{ display:inline !important; }'],
  // tslint:disable-next-line:max-line-length
  template: '<count-widget class="countwidget" title={{title}} count={{count}} label={{label}} extracount={{extracount}} extralabel={{extralabel}} detail={{detail}} color={{color}} route={{route}}></count-widget>',
})
export class SubjectCountWidgetComponent implements OnInit, OnDestroy {

  count: number;
  title: string;
  label: string;
  extralabel: string;
  extracount: number;
  color: string;
  detail: string;
  route: string;
  prev: number[];
  users: number[] = [];

  _settings: CountWidgetSettings;

  selectedUsersChanged: Subscription;
  statisticsUpdated: Subscription;

  @Input() set settings(val: CountWidgetSettings) {
    if (val) {
      const settings = this._settings;
      this._settings = val || new CountWidgetSettings();
      if (this._settings) {
        this._settings.title = (this._settings.title || '').trim();
        this._settings.label = (this._settings.label || '').trim();
        this._settings.extraLabel = (this._settings.extraLabel || '').trim();
      }
      this.setupWidget();
      this.setUserInfo();
      this.prev = [];
      if (this._settings) {
        this.updateCounts();
      }
    }
  }

  // added to suppress error messages
  @Input() id;
  @Input() subjectId;
  @Input() processTypeId;

  constructor(
    private statisticsService: StatisticsService,
    private subjectTypeService: SubjectTypeService,
    private organisationService: OrganisationService,
    private stateService: StateService,
    private statusService: StatusService,
    private labelService: LabelService,
    private categoryService: CategoryService,
    private lookupService: LookupService,
    private userService: UserService) {
    this.selectedUsersChanged = this.userService.onSelectedUsersChanged.subscribe((users) => {
      this.users = users;
      this.updateWidget(users);
    });
    this.statisticsUpdated = this.statisticsService.onStatisticsUpdated.subscribe((_) => { this.prev = []; });
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.selectedUsersChanged) {
      this.selectedUsersChanged.unsubscribe();
    }
    if (this.statisticsUpdated) {
      this.statisticsUpdated.unsubscribe();
    }
  }

  updateWidget(users?) {
    if (this._settings) {
      this._settings.users = users;
      this.prev = users;
      this.setUserInfo();
      this.updateCounts();
    }
  }

  setupWidget() {
    const settings = this._settings;
    this.label = settings ? settings.label || this.labelService.getDefaultCountLabel(settings).toUpperCase() : '';
    this.count = 0;
    this.color = (settings && settings.color) ? settings.color : 'rgb(244, 67, 54)';

    this.extralabel = '';
    this.extracount = 0;
    if (settings && settings.showExtra) {
      this.extralabel = settings.extraLabel || this.labelService.getDefaultExtraCountLabel(settings);
    }

    this.detail = 'More information to go here...';
  }

  setUserInfo() {
    const settings = this._settings;
    const users = this.getUsers(settings);
    this.title = settings.title || this.labelService.getDefaultTitle(settings, users);
    const subjectType = (settings.typeId ? this.subjectTypeService.getName(settings.typeId) : '');
    this.route = this.getRoute(settings);
  }

  updateCounts() {
    const settings = this._settings;
    const context = this.labelService.getContext(settings.context);
    const status = this.getStatus(settings.status);
    const extraStatus = this.getStatus(settings.extraStatus);

    const category = this.getStatus(settings.category);
    const extraCategory = this.getStatus(settings.extraCategory);

    const caseType = this.getStatus(settings.caseType);
    const extraCaseType = this.getStatus(settings.extraCaseType);

    const users = this.getUsers(settings);
    this.count =
      settings && settings.typeId
        ? this.statisticsService.getSubjectSnapshotStat(
            settings.typeId,
            settings.referralCounts,
            settings.stateId,
            status,
            category,
            caseType,
            settings.organisationId,
            users,
            context
          )
        : 0;

    // console.log(`Updating count for ${this.title}`, this.count, this._settings, this.users);

    if (settings && settings.showExtra) {
      this.extracount = this.statisticsService.getSubjectSnapshotStat(
        settings.extraTypeId,
        settings.referralCounts,
        settings.extraStateId,
        extraStatus,
        extraCategory,
        extraCaseType,
        settings.organisationId,
        users,
        context
      );
    }
    else {
      this.extralabel = '';
      this.extracount = 0;
    }
  }

  getStatus(status): number[] | undefined {
    if (status) {
      const arr = (Array.isArray(status) ? status : status.split(','));
      return arr.map((i) => Number(i));
    }
    return undefined;
  }

  onlyCurrentUser(users: number[]): boolean {
    if (users) {
      return ((users.length === 1) && (users[0] === this.userService.getCurrentUserId()));
    }
    else {
      return false;
    }
  }

  getRoute(settings: CountWidgetSettings) {
    const users = this.getUsers(settings);
    const subjectType = (settings.typeId ? this.subjectTypeService.getName(settings.typeId) : '');

    let route = `/${settings.referralCounts ? 'referrals' : 'subjects'}/${subjectType}/u`;
    let routeUser = 'all';
    if (settings) {
      if (settings.context && (settings.context.length > 0)) {
        const context = this.labelService.getContext(settings.context);
        routeUser = this.getRouteContext(context) || 'all';
        if ((routeUser === 'users') && this.onlyCurrentUser(users)) {
          routeUser = 'my';
        }
      }

      route += `/${routeUser}`;

      if (settings.stateId > 0) {
        route += '/state/' + this.stateService.getName(settings.stateId);
      }

      if (settings.status && (!(settings.stateId && (settings.stateId > 0)))) {
        route += '/status/' + this.getRouteStatus(settings.status);
      }

      if (settings.category && (!(settings.stateId && (settings.stateId > 0)))) {
        route += '/category/' + this.getRouteCategory(settings.category);
      }

      if (settings.caseType && (!(settings.stateId && (settings.stateId > 0)))) {
        route += '/caseType/' + this.getRouteCaseType(settings.caseType);
      }

      if (settings.organisationId > 0) {
        route += '/' + this.organisationService.getName(settings.organisationId).toLowerCase();
      }
    }
    else {
      route += `/all`;
    }
    return route;
  }

  getRouteContext(context: number[]) {
    const result: string[] = [];
    let altResult = 'all';
    if (context && (context.length > 0)) {
      context.forEach((i) => {
        switch (i) {
          case -2: altResult = 'managers'; break;
          case -1: altResult = 'users'; break;
          case 0: altResult = 'all'; break;
          default:
            this.userService.getById(i).subscribe((user: User) => {
              if (user) {
                result.push(user.uid);
              }
            });
            break;
        }
      });
      return (result.length > 0) ? result.join(',') : altResult;
    }
  }

  getRouteStatus(status: string) {
    const result: string[] = [];
    if (status) {
      const arr: any[] = Array.isArray(status) ? status : status.split(',');
      arr.forEach((i) => {
        if (Number(i) > 0) {
          const uid = this.statusService.getUid(Number(i));
          if (uid) {
            result.push(uid);
          }
        }
        else {
          result.push('Blank');
        }
      });
    }
    return result.join(',');
  }

  getRouteCategory(category: string) {
    const result: string[] = [];
    if (category) {
      const arr: any[] = Array.isArray(category) ? category : category.split(',');
      arr.forEach((i) => {
        if (Number(i) > 0) {
          const uid = this.categoryService.getUid(Number(i));
          if (uid) {
            result.push(uid);
          }
        }
        else {
          result.push('Blank');
        }
      });
    }
    return result.join(',');
  }

  getRouteCaseType(caseType: string) {
    const result: string[] = [];
    if (caseType) {
      const arr: any[] = Array.isArray(caseType) ? caseType : caseType.split(',');
      arr.forEach((i) => {
        if (Number(i) > 0) {
          const uid = this.lookupService.getUid(Number(i));
          if (uid) {
            result.push(uid);
          }
        }
        else {
          result.push('Blank');
        }
      });
    }
    return result.join(',');
  }

  getUsers(settings) {
    let result = [];
    if (settings) {
      result = settings.users || this.users;
      const context = this.labelService.getContext(settings.context);
      if (context && (context.length > 0) && (context[0] < 1)) {
        result = (this.users.length > 0) ? this.users : (settings.users || []);
      }
      return result;
    }
    else {
      return this.users;
    }
  }
}
